import { Box, Button, HStack, Text, VStack } from "@chakra-ui/react"
import { useToolbarPosition } from "@pathwright/ux/src/components/augmentation/AugmentationPositionedToolbar"
import { DarkMode } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import isEqual from "react-fast-compare"
import { useClickAway, useIntersection, usePrevious } from "react-use"
import { usePathCreatorContext } from "../../path/create/PathCreatorContext"
import PathCreatorFlow from "../../path/create/PathCreatorFlow"
import { OnComplete } from "../../path/create/PathCreatorForm"
import Pathicon from "../../pathicon/Pathicon"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import useAddPathInitialValue from "./useAddPathInitialValue"

type AddPathButtonProps = {
  onComplete?: OnComplete
  // Whether the PathCreatorForm inputs should auto show their related tooltip when
  // user focuses the input.
  autoShowTips?: boolean
}

// 3 flow states: "hidden", "animating", "active".
// "hidden" - flow is not visible, "Add Path" button is visible.
// "animating" - "Add Path" button is scrolled into view
// "active" - flow is visible, "Add Path" button is hidden.

const AddPathButton = ({ onComplete, autoShowTips }: AddPathButtonProps) => {
  const { me } = usePathwrightContext()
  const { t } = useTranslate()
  // TODO: use setInitialValue to reset after flow abandoned or completed.
  const { initialValue, setInitialValue } = usePathCreatorContext()
  const [flowState, setFlowState] = useState<"hidden" | "animating" | "active">(
    "hidden"
  )

  // Hide the button when toolbar is positioned at the bottom of the screen.
  // The toolbar actually renders an alternate add path button in this case,
  // which, when clicked, will trigger the flow in this component.
  const toolbarAtSide = useToolbarPosition(
    {
      bottom: false,
      side: true
    },
    true /* allowOverlay */
  )

  // On mobile, the toolbar is fixed at the bottom and overlaps content.
  // Add padding so the form/gate clears the toolbar.
  const toolbarBottomPadding = useToolbarPosition(
    {
      bottom: "80px",
      side: "0px"
    },
    true /* allowOverlay */
  )
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const flowRef = useRef<HTMLDivElement>(null)
  const intersectionRef = useRef<HTMLDivElement>(null)
  const intersection = useIntersection(intersectionRef, {
    root: null,
    rootMargin: "0px",
    threshold: 0
  })

  const {
    setInitialValue: setInitialValueForFlow,
    loading: loadingInitialValue
  } = useAddPathInitialValue()

  const showFlow = flowState !== "hidden" && !loadingInitialValue

  const showButton =
    toolbarAtSide && (!intersection?.isIntersecting || !showFlow)

  const buttonIsLoading = loadingInitialValue || flowState === "animating"

  // Track if a react-select listbox is present in the DOM, including portaled
  // menus that render outside flowRef. While a listbox is open, ignore click-away
  // so choosing roles cannot be mistaken for discarding the flow.
  const [hasSelectListbox, setHasSelectListbox] = useState(false)

  useEffect(() => {
    if (flowState === "active") {
      const checkListbox = () => {
        setHasSelectListbox(
          !!document.querySelector("[id^='react-select-'][id$='-listbox']")
        )
      }

      checkListbox()
      const observer = new MutationObserver(checkListbox)
      observer.observe(document.body, {
        childList: true,
        subtree: true
      })

      return () => {
        observer.disconnect()
        setHasSelectListbox(false)
      }
    }
  }, [flowState])

  // If user clicks outside then closes out the flow, prompt to discard.
  // Ignore portaled select menus and field tips that belong to this flow.
  useClickAway(flowRef, (event) => {
    if (flowState !== "active" || hasSelectListbox || showButton) return

    const target = event.target
    if (target instanceof Element) {
      if (target.closest("[id^='react-select-'][id$='-listbox']")) return
      if (
        target.closest(
          ".TooltipContent, [data-scope='popover'], [data-scope='hover-card']"
        )
      ) {
        return
      }
    }

    setConfirmDiscard(true)
  })

  function scrollIntoView() {
    intersectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center"
    })
  }

  useLayoutEffect(() => {
    if (flowState === "animating" && !loadingInitialValue) {
      scrollIntoView()
      if (intersection?.isIntersecting) {
        // Once flow comes into view, apply the deferred flow props.
        setFlowState("active")
      }
    }
  }, [flowState, intersection?.isIntersecting, loadingInitialValue])

  // Animate the flow into view when initialValue is set.
  const prevInitialValue = usePrevious(initialValue)
  useEffect(() => {
    if (initialValue === undefined) {
      setFlowState("hidden")
    } else if (!isEqual(initialValue, prevInitialValue)) {
      setFlowState("animating")
    }
  }, [initialValue, prevInitialValue])

  return (
    <>
      {/* Render an element to scroll into view. Seems necessary for smooth scrolling. */}
      <Box ref={intersectionRef} w="100%">
        {showFlow && (
          <Box pos="relative" w="100%" ref={flowRef}>
            <PathCreatorFlow
              targetUserId={me.id!}
              initialValue={initialValue}
              setInitialValue={setInitialValue}
              onComplete={async (data) => {
                await onComplete?.(data)
                setFlowState("hidden")
                setInitialValue()
              }}
              autoShowTips={autoShowTips}
            />
            {confirmDiscard && (
              <VStack
                justify="center"
                pos="absolute"
                inset={0}
                bg="blackAlpha.600"
                h="100%"
                borderRadius="xl"
                backdropFilter="blur(20px)"
                zIndex={100}
                gap={4}
              >
                <Text color="white" fontSize="sm" m={0}>
                  Cancel adding this Path?
                </Text>
                {/* VStack in wireframes, HStack in real world (since less room in initial Path create/select step
              though click outside should just automatically discard in that case, but we didn't hoist state, so we're in the dark.) */}
                <HStack>
                  <Button
                    size="sm"
                    variant="subtle"
                    onClick={() => {
                      setFlowState("hidden")
                      setConfirmDiscard(false)
                      setInitialValue()
                    }}
                    autoFocus
                  >
                    Yes, cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorPalette="whiteAlpha"
                    onClick={() => setConfirmDiscard(false)}
                  >
                    No, go back
                  </Button>
                </HStack>
              </VStack>
            )}
          </Box>
        )}
      </Box>

      {showButton && (
        <DarkMode>
          <Button
            onClick={() => {
              scrollIntoView()
              setFlowState("animating")
              if (flowState === "hidden") {
                setInitialValueForFlow()
              }
            }}
            w="auto"
            m="0 auto"
            colorPalette="brand"
            shadow="rgba(0, 0, 0, .2) 0 0 10px 5px"
            backdropFilter="blur(20px)"
            position="sticky"
            bottom={3}
            zIndex={10011}
            loading={buttonIsLoading}
          >
            <Pathicon icon="plus" />
            {t("Add Path")}
          </Button>
        </DarkMode>
      )}

      {/* Render an element to scroll into view. Seems necessary for smooth scrolling. */}
      {/* TODO: ensure we include enough height for the select menus */}
      {/* (calculate in some way)? */}
      {/* NOTE: only including margin bottom when form is active, mainly */}
      {/* to avoid extra space when registrations list hasn't finished loading. */}
      {flowState !== "hidden" && <Box h="400px" w="100%" />}
    </>
  )
}

export default AddPathButton
